import { ResearchCompanyButton } from '@/features/ai/ai-pages';
import { AssociationsSection, CustomPropertiesSection, HistoryButton, RecordTags, type ObjectType } from './records';
import { usePermission } from '@/hooks/use-permission';
import { AiRecordSummary } from '@/features/ai/ai';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRightLeft, Briefcase, ChevronLeft, Mail, Pencil, Phone } from 'lucide-react';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { ErrorState } from '@/components/shared/page';
import { RecordSection } from '@/components/shared/record-section';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, Skeleton } from '@/components/ui/misc';
import { formatDate, formatMoney } from '@/lib/format';
import { RecordSalesActivity, SalesToolbar } from '@/features/sales/record-tools';
import { WebsiteVisitsCard } from '@/features/growth/visits-card';
import { LinkedObjectsCard } from '@/features/objects/objects-page';
import { TimelinePanel } from './deal-detail-page';
import { LeadDialog } from './lead-dialog';
import { ConvertLeadDialog } from './convert-lead-dialog';
import { ContactDialog } from './contacts-page';
import { CompanyDialog } from './companies-page';
import { useCompany, useContact, useLead } from './api';
import {
  CAPTURE_CHANNEL_LABELS,
  COMPANY_TYPE_LABELS,
  LEAD_SOURCE_LABELS,
  LEAD_STATUS_LABELS,
  LIFECYCLE_LABELS,
} from './labels';

/** A label above its value, as in the About panel; nothing is drawn for blanks. */
function Detail({ label, children }: { label: string; children: ReactNode }) {
  const t = useT();
  if (children === null || children === undefined || children === '') return null;
  return (
    <div className="py-2">
      <div className="text-xs text-muted-foreground">{t(label)}</div>
      <div className="mt-0.5 text-sm font-medium [overflow-wrap:anywhere]">{children}</div>
    </div>
  );
}

function Shell({
  title,
  subtitle,
  breadcrumbs,
  actions,
  toolbar,
  details,
  overview,
  timeline,
  extra,
  badge,
  square,
  record,
}: {
  title: string;
  subtitle?: ReactNode;
  breadcrumbs: { label: string; to?: string }[];
  actions?: ReactNode;
  toolbar: ReactNode;
  details: ReactNode;
  overview: ReactNode;
  timeline: ReactNode;
  extra?: ReactNode;
  badge?: ReactNode;
  /** Companies get a square tile rather than a round avatar. */
  square?: boolean;
  /** Which record this is, for tags, custom properties and history. */
  record: { type: ObjectType; id: string; customFields?: Record<string, unknown> | null };
}) {
  const t = useT();
  const [tab, setTab] = useState<'overview' | 'activities'>('activities');
  const { can } = usePermission();
  const back = breadcrumbs[0];
  return (
    <div className="grid items-start gap-4 lg:grid-cols-[19rem_minmax(0,1fr)] xl:grid-cols-[20rem_minmax(0,1fr)_19rem]">
      {/* Left: who this is, the quick actions, and every property. */}
      <aside className="space-y-3 lg:sticky lg:top-0">
        <div className="rounded-lg border bg-card">
          <div className="flex items-center justify-between border-b px-3 py-2">
            {back?.to ? (
              <Link to={back.to} className="text-link inline-flex items-center gap-1 text-sm font-semibold hover:underline">
                <ChevronLeft className="h-4 w-4" />
                {t(back.label)}
              </Link>
            ) : (
              <span />
            )}
            {actions && <div className="flex items-center gap-1.5 [&_button]:h-8 [&_button]:px-3 [&_button]:text-xs [&_a]:h-8 [&_a]:px-3 [&_a]:text-xs">{actions}</div>}
          </div>
          <div className="px-4 pb-4 pt-4">
            <div className="flex items-start gap-3">
              {square ? (
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border bg-muted text-lg font-bold text-muted-foreground">
                  {title.slice(0, 1).toUpperCase()}
                </span>
              ) : (
                <Avatar name={title} size={48} />
              )}
              <div className="min-w-0">
                <h1 className="text-xl font-bold leading-tight [overflow-wrap:anywhere]">{title}</h1>
                {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
                {badge && <div className="mt-1.5">{badge}</div>}
                <div className="mt-2">
                  <RecordTags type={record.type} id={record.id} canEdit={can(`crm.${record.type}.update`)} />
                </div>
              </div>
            </div>
            <div className="mt-4">{toolbar}</div>
          </div>
        </div>
        {details}
        <CustomPropertiesSection type={record.type} id={record.id} values={record.customFields} canEdit={can(`crm.${record.type}.update`)} />
        <div className="px-1">
          <HistoryButton type={record.type} id={record.id} />
        </div>
      </aside>

      {/* Middle: what has happened and what is next. */}
      <div className="min-w-0">
        <div className="mb-3 flex rounded-lg border bg-card p-1">
          {(['activities', 'overview'] as const).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={cn(
                'flex-1 rounded-md py-2 text-sm font-semibold transition-colors',
                tab === key ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {key === 'overview' ? t('Sales tools & insights') : t('Activities')}
            </button>
          ))}
        </div>
        <div className="space-y-4">
          {tab === 'overview' ? overview : timeline}
        </div>
      </div>

      {/* Right: the records this one is associated with. */}
      <aside className="space-y-3 lg:col-span-2 xl:col-span-1">{extra}</aside>
    </div>
  );
}

function Loading() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-10 w-72" />
      <Skeleton className="h-8 w-96" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

// ── Lead ─────────────────────────────────────────────────────────────────────

export function LeadRecordPage() {
  const { leadId } = useParams();
  const { data: lead, isLoading, error, refetch } = useLead(leadId);
  const [editing, setEditing] = useState(false);
  const [converting, setConverting] = useState(false);

  if (isLoading) return <Loading />;
  if (error || !lead) return <ErrorState message="This lead could not be loaded." onRetry={() => void refetch()} />;
  const converted = lead.status === 'CONVERTED';

  return (
    <Shell
      title={lead.name}
      subtitle={[lead.number, lead.designation, lead.companyName].filter(Boolean).join(' · ')}
      breadcrumbs={[{ label: 'Leads', to: '/crm/leads' }, { label: lead.number }]}
      badge={<Badge variant="secondary">{LEAD_STATUS_LABELS[lead.status]}</Badge>}
      record={{ type: 'lead', id: lead.id, customFields: lead.customFields }}
      actions={
        <>
          {!converted && (
            <Can permission="crm.lead.update">
              <Button variant="outline" onClick={() => setEditing(true)}>
                <Pencil /> Edit
              </Button>
              <Button onClick={() => setConverting(true)}>
                <ArrowRightLeft /> Convert
              </Button>
            </Can>
          )}
          {converted && lead.convertedDealId && (
            <Button asChild>
              <Link to={`/crm/deals/${lead.convertedDealId}`}>
                <Briefcase /> Open deal
              </Link>
            </Button>
          )}
        </>
      }
      toolbar={
        <SalesToolbar
          variant="round"
          record={{ entityType: 'lead', entityId: lead.id, name: lead.name, email: lead.email, phone: lead.phone, whatsapp: lead.whatsapp }}
        />
      }
      overview={
        <>
          <AiRecordSummary entityType="lead" entityId={lead.id} />
          <RecordSalesActivity entityType="lead" entityId={lead.id} />
        </>
      }
      timeline={<TimelinePanel entityType="lead" entityId={lead.id} />}
      details={
        <RecordSection title="About this lead">
            <Detail label="Email">{lead.email && <a className="text-link hover:underline" href={`mailto:${lead.email}`}>{lead.email}</a>}</Detail>
            <Detail label="Phone">{lead.phone && <a className="text-link hover:underline" href={`tel:${lead.phone}`}>{lead.phone}</a>}</Detail>
            <Detail label="WhatsApp">{lead.whatsapp}</Detail>
            <Detail label="Company">{lead.companyName}</Detail>
            <Detail label="Stage">{lead.stage?.name}</Detail>
            <Detail label="Source">{LEAD_SOURCE_LABELS[lead.source]}</Detail>
            <Detail label="Came in through">{lead.captureChannel ? CAPTURE_CHANNEL_LABELS[lead.captureChannel] ?? lead.captureChannel : null}</Detail>
            <Detail label="Campaign">{lead.campaign}</Detail>
            <Detail label="Budget">{lead.budget ? formatMoney(lead.budget, lead.currency) : null}</Detail>
            <Detail label="Location">{[lead.city, lead.country].filter(Boolean).join(', ')}</Detail>
            <Detail label="Score">{lead.score}</Detail>
            <Detail label="Owner">{lead.owner?.name ?? 'Unassigned'}</Detail>
            <Detail label="Created">{formatDate(lead.createdAt)}</Detail>
            {lead.requirement && <Detail label="Requirement"><span className="whitespace-pre-wrap font-normal">{lead.requirement}</span></Detail>}
          {editing && <LeadDialog lead={lead} onClose={() => setEditing(false)} />}
          {converting && <ConvertLeadDialog lead={lead} onClose={() => setConverting(false)} />}
        </RecordSection>
      }
      extra={
        <>
          <AssociationsSection type="lead" id={lead.id} />
          <LinkedObjectsCard entityType="lead" entityId={lead.id} />
          <WebsiteVisitsCard entityType="lead" entityId={lead.id} />
        </>
      }
    />
  );
}

// ── Contact ──────────────────────────────────────────────────────────────────

export function ContactRecordPage() {
  const { contactId } = useParams();
  const { data: contact, isLoading, error, refetch } = useContact(contactId);
  const [editing, setEditing] = useState(false);

  if (isLoading) return <Loading />;
  if (error || !contact) return <ErrorState message="This contact could not be loaded." onRetry={() => void refetch()} />;

  return (
    <Shell
      title={contact.name}
      subtitle={[contact.designation, contact.company?.name].filter(Boolean).join(' · ')}
      breadcrumbs={[{ label: 'Contacts', to: '/crm/contacts' }, { label: contact.name }]}
      record={{ type: 'contact', id: contact.id, customFields: contact.customFields }}
      actions={
        <Can permission="crm.contact.update">
          <Button variant="outline" onClick={() => setEditing(true)}>
            <Pencil /> Edit
          </Button>
        </Can>
      }
      toolbar={
        <SalesToolbar
          variant="round"
          record={{
            entityType: 'contact',
            entityId: contact.id,
            name: contact.name,
            email: contact.email,
            phone: contact.phone,
            whatsapp: contact.whatsapp,
          }}
        />
      }
      overview={
        <>
          <AiRecordSummary entityType="contact" entityId={contact.id} />
          <RecordSalesActivity entityType="contact" entityId={contact.id} />
        </>
      }
      timeline={<TimelinePanel entityType="contact" entityId={contact.id} />}
      details={
        <RecordSection title="About this contact">
            <Detail label="Email">{contact.email && <a className="text-link hover:underline" href={`mailto:${contact.email}`}>{contact.email}</a>}</Detail>
            <Detail label="Phone">{contact.phone && <a className="text-link hover:underline" href={`tel:${contact.phone}`}>{contact.phone}</a>}</Detail>
            <Detail label="WhatsApp">{contact.whatsapp}</Detail>
            <Detail label="Company">
              {contact.company && (
                <Link className="text-link hover:underline" to={`/crm/companies/${contact.company.id}`}>
                  {contact.company.name}
                </Link>
              )}
            </Detail>
            <Detail label="Contact owner">{contact.owner?.name ?? 'No owner'}</Detail>
            <Detail label="Department">{contact.department}</Detail>
            <Detail label="LinkedIn">{contact.linkedin && <a className="text-link hover:underline" href={contact.linkedin} target="_blank" rel="noreferrer">Profile</a>}</Detail>
            <Detail label="Decision maker">{contact.isDecisionMaker ? 'Yes' : null}</Detail>
            <Detail label="Email opt-in">{contact.emailOptIn ? 'Yes' : 'No'}</Detail>
            <Detail label="WhatsApp opt-in">{contact.whatsappOptIn ? 'Yes' : 'No'}</Detail>
            <Detail label="Deals">{contact.dealCount}</Detail>
            <Detail label="Added">{formatDate(contact.createdAt)}</Detail>
          {editing && <ContactDialog contact={contact} onClose={() => setEditing(false)} />}
        </RecordSection>
      }
      extra={
        <>
          <RecordSection title="Companies" count={contact.company ? 1 : 0}>
            {contact.company ? (
              <Link to={`/crm/companies/${contact.company.id}`} className="mt-2 block rounded-md border p-3 text-sm hover:bg-accent">
                <span className="text-link font-semibold">{contact.company.name}</span>
                {contact.isPrimary && <Badge variant="success" className="ms-2">Primary</Badge>}
              </Link>
            ) : (
              <p className="py-3 text-sm text-muted-foreground">No company yet.</p>
            )}
          </RecordSection>
          <AssociationsSection type="contact" id={contact.id} />
          <LinkedObjectsCard entityType="contact" entityId={contact.id} />
          <WebsiteVisitsCard entityType="contact" entityId={contact.id} />
        </>
      }
    />
  );
}

// ── Company ──────────────────────────────────────────────────────────────────

export function CompanyRecordPage() {
  const { companyId } = useParams();
  const { data: company, isLoading, error, refetch } = useCompany(companyId);
  const [editing, setEditing] = useState(false);

  if (isLoading) return <Loading />;
  if (error || !company) return <ErrorState message="This company could not be loaded." onRetry={() => void refetch()} />;
  const primary = company.contacts?.find((contact) => contact.email) ?? company.contacts?.[0];

  return (
    <Shell
      title={company.name}
      subtitle={[COMPANY_TYPE_LABELS[company.type], company.industry, company.website].filter(Boolean).join(' · ')}
      breadcrumbs={[{ label: 'Companies', to: '/crm/companies' }, { label: company.name }]}
      record={{ type: 'company', id: company.id, customFields: company.customFields }}
      actions={
        <Can permission="crm.company.update">
          <ResearchCompanyButton companyId={company.id} />
          <Button variant="outline" onClick={() => setEditing(true)}>
            <Pencil /> Edit
          </Button>
        </Can>
      }
      toolbar={
        <SalesToolbar
          variant="round"
          record={{
            entityType: 'company',
            entityId: company.id,
            name: company.name,
            email: primary?.email ?? null,
            phone: primary?.phone ?? null,
            whatsapp: primary?.whatsapp ?? null,
          }}
        />
      }
      overview={
        <>
          <AiRecordSummary entityType="company" entityId={company.id} />
          <RecordSalesActivity entityType="company" entityId={company.id} />
        </>
      }
      timeline={<TimelinePanel entityType="company" entityId={company.id} />}
      square
      details={
        <>
          <RecordSection title="About this company">
              <Detail label="Lifecycle">{LIFECYCLE_LABELS[company.lifecycleStage]}</Detail>
              <Detail label="Website">{company.website && <a className="text-link hover:underline" href={company.website} target="_blank" rel="noreferrer">{company.website.replace(/^https?:\/\//, '')}</a>}</Detail>
              <Detail label="TRN">{company.trn}</Detail>
              <Detail label="Trade licence">{company.registrationNo}</Detail>
              <Detail label="Payment terms">{`${company.paymentTermsDays} days`}</Detail>
              <Detail label="Currency">{company.currency}</Detail>
              <Detail label="Owner">{company.owner?.name ?? 'Unassigned'}</Detail>
              <Detail label="Since">{formatDate(company.createdAt)}</Detail>
          </RecordSection>
          {editing && <CompanyDialog company={company} onClose={() => setEditing(false)} />}
        </>
      }
      extra={
        <>
          <RecordSection title="Contacts" count={(company.contacts ?? []).length}>
            <div className="space-y-2 pt-2">
              {(company.contacts ?? []).length === 0 && <p className="py-1 text-sm text-muted-foreground">No contacts yet.</p>}
              {(company.contacts ?? []).map((contact) => (
                <Link key={contact.id} to={`/crm/contacts/${contact.id}`} className="flex items-start gap-2.5 rounded-md border p-2.5 text-sm hover:bg-accent">
                  <Avatar name={contact.name} size={28} />
                  <span className="min-w-0">
                    <span className="text-link block truncate font-semibold">{contact.name}</span>
                    {contact.designation && <span className="block truncate text-xs text-muted-foreground">{contact.designation}</span>}
                    {contact.email && <span className="flex items-center gap-1 truncate text-xs text-muted-foreground"><Mail className="h-3 w-3" />{contact.email}</span>}
                    {contact.phone && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Phone className="h-3 w-3" /><span dir="ltr">{contact.phone}</span></span>}
                  </span>
                </Link>
              ))}
            </div>
          </RecordSection>
          <RecordSection title="Deals" count={(company.deals ?? []).length}>
            <div className="space-y-2 pt-2">
              {(company.deals ?? []).length === 0 && <p className="py-1 text-sm text-muted-foreground">No deals yet.</p>}
              {(company.deals ?? []).map((deal) => (
                <Link key={deal.id} to={`/crm/deals/${deal.id}`} className="block rounded-md border p-2.5 text-sm hover:bg-accent">
                  <span className="text-link block truncate font-semibold">{deal.title}</span>
                  <span className="tabular text-xs text-muted-foreground">{formatMoney(deal.value, deal.currency)}</span>
                </Link>
              ))}
            </div>
          </RecordSection>
          <AssociationsSection type="company" id={company.id} />
          <LinkedObjectsCard entityType="company" entityId={company.id} />
        </>
      }
    />
  );
}
